import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import IconButton from "@mui/material/IconButton";
import Typography from "@mui/material/Typography";

import DeleteIcon from "@mui/icons-material/Delete";

import Expandable from "lib/components/expand/Expandable";

import { DeleteEvent, Event, EventGroup, MoveEvent } from "utils/history/Event";
import TableActionbar from "utils/list/TableActionbar";
import { ListInfo } from "utils/list/List";
import { asTitle } from "utils/Utils";

import { CompetenceListFieldLevel, FieldType } from "core/field/FieldService";
import FieldTable from "core/field/FieldTable";

export type CompetenceListFieldLevelTableProps = {
	level: CompetenceListFieldLevel;
	editable: boolean;

	onChange: (event: Event) => void;
};

const CompetenceListFieldLevelTable: React.FC<
	CompetenceListFieldLevelTableProps
> = (props: CompetenceListFieldLevelTableProps) => {
	const ELEMENTS_FIELD = "/elements";
	const ELEMENTS_PATH = ELEMENTS_FIELD + "/";

	const { t } = useTranslation();

	const [open, setOpen] = useState(false);
	const [listInfo, setListInfo] = useState<ListInfo<string>>(ListInfo.init());

	useEffect(() => {
		setListInfo(prevInfo =>
			prevInfo.withKeys(props.level.elements.map(field => field.uuid))
		);
	}, [props.level.elements]);

	function changeField(event: Event): void {
		event.applyContext(ELEMENTS_FIELD);
		props.onChange(event);
	}

	function selectFields(keys: string[], select: boolean): void {
		setListInfo(listInfo.markSelected(keys, select));
	}

	function handleMoveUp(): void {
		const events: Event[] = [];
		for (const [index, field] of props.level.elements.entries()) {
			if (listInfo.isVisibleAndSelected(field.uuid)) {
				events.push(
					new MoveEvent(
						ELEMENTS_PATH + index,
						ELEMENTS_PATH + (index - 1)
					)
				);
			}
		}
		const event = new EventGroup(events);
		props.onChange(event);
	}

	function handleMoveDown(): void {
		const events: Event[] = [];
		for (const [index, field] of props.level.elements.entries()) {
			if (listInfo.isVisibleAndSelected(field.uuid)) {
				events.unshift(
					new MoveEvent(
						ELEMENTS_PATH + index,
						ELEMENTS_PATH + (index + 1)
					)
				);
			}
		}
		const event = new EventGroup(events);
		props.onChange(event);
	}

	function handleDelete(): void {
		const events: Event[] = [];
		for (const [index, field] of props.level.elements.entries()) {
			if (listInfo.isVisibleAndSelected(field.uuid)) {
				events.unshift(new DeleteEvent(ELEMENTS_PATH + index, field));
			}
		}
		const event = new EventGroup(events);
		props.onChange(event);
	}

	function deleteLevel(): void {
		const event = new DeleteEvent("", props.level);
		props.onChange(event);
	}

	function getSupportedTypes(): FieldType[] {
		return [
			FieldType.BOOLEAN,
			FieldType.DATE,
			FieldType.DECIMAL,
			FieldType.GROUP,
			FieldType.INTEGER,
			FieldType.IMAGE,
			FieldType.RATING,
			FieldType.SELECT,
			FieldType.MULTI_SELECT,
			FieldType.TEXT,
			FieldType.URL,
			FieldType.UPLOAD
		];
	}

	return (
		<Expandable
			title={
				<Typography variant="subtitle2">
					{asTitle(t("level"))} {props.level.level}
				</Typography>
			}
			adornment={
				<IconButton
					size="small"
					disabled={!props.editable}
					onClick={deleteLevel}>
					<DeleteIcon />
				</IconButton>
			}>
			<TableActionbar
				listInfo={listInfo}
				onMoveUp={handleMoveUp}
				onMoveDown={handleMoveDown}
				onDelete={handleDelete}
			/>
			<FieldTable
				supportedTypes={getSupportedTypes()}
				elements={props.level.elements}
				listInfo={listInfo}
				editable={props.editable}
				onSelect={selectFields}
				onChange={changeField}
				onInsert={changeField}
			/>
		</Expandable>
	);
};

export default CompetenceListFieldLevelTable;
